import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * 3 + 2 bundle: when the bundle is chosen and all five shades are picked, the add to cart button adds the five
 * pens (one cart line per shade) plus the two free gifts (the $0 "Mystery Gift" and "Long-Lasting Kit" products)
 * in one request. The 2 free pens come from the store's automatic "Buy 3 get 2" discount. The cart drawer is
 * refreshed through the same events the theme's own add to cart uses.
 */

const MAIN_BUTTON = '.product-details .product-form-buttons .add-to-cart-button';
const CARD = '.product-details .offer-card';

/** @returns {{ shades: string[], gifts: number[] } | null} */
function bundleChoice() {
  const card = document.querySelector(CARD);
  if (!(card instanceof HTMLElement)) return null;

  const checked = card.querySelector('input[type="radio"]:checked');
  if (!(checked instanceof HTMLInputElement) || checked.value === '1') return null;

  const picks = card.querySelector('.offer-card__picks');
  const shades = picks instanceof HTMLElement ? (picks.dataset.shades ?? '').split('|').filter(Boolean) : [];
  if (shades.length !== 5) return null;

  const gifts = (card.dataset.giftVariants ?? '')
    .split(',')
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);

  return { shades, gifts };
}

/** @param {string} shade */
async function variantOf(shade) {
  const response = await fetch(`/products/${encodeURIComponent(shade.toLowerCase())}-nail-pen.js`);
  if (!response.ok) throw new Error(`Shade ${shade} not found`);
  const product = await response.json();
  const variant = product.variants.find((/** @type {{ available: boolean }} */ v) => v.available) ?? product.variants[0];
  return { id: Number(variant.id), productId: Number(product.id) };
}

async function addBundle(/** @type {HTMLElement} */ button, /** @type {{ shades: string[], gifts: number[] }} */ choice) {
  const counts = new Map();
  for (const shade of choice.shades) counts.set(shade, (counts.get(shade) ?? 0) + 1);

  const variants = await Promise.all([...counts.keys()].map((shade) => variantOf(shade)));
  const pens = [...counts.values()].map((quantity, index) => ({
    id: variants[index].id,
    quantity,
    properties: { _bundle: '3+2' },
  }));
  const gifts = choice.gifts.map((id) => ({ id, quantity: 1, properties: { _free_gift: 'bundle' } }));
  // The cart lists the most recently added line first, so add the gifts first: the nail pens then show on top.
  const items = [...gifts, ...pens];

  const sections = [...document.querySelectorAll('cart-items-component')]
    .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : ''))
    .filter(Boolean)
    .join(',');

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections }),
    });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || result.message || 'Add to cart failed');

    const cart = await (await fetch('/cart.js')).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'plum-bundle',
        sourceId: 'plum-bundle',
        itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        productId: String(variants[0].productId),
        sections: result.sections,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    button.dispatchEvent(
      new CartErrorEvent({ error: error instanceof Error ? error.message : 'Add to cart failed', code: 'INVALID' })
    );
  }
}

// Window capture: runs before the theme's own click handling of the main button.
window.addEventListener(
  'click',
  async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest(MAIN_BUTTON);
    if (!(button instanceof HTMLElement)) return;

    const choice = bundleChoice();
    if (!choice) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (button.dataset.busy === 'true') return;
    button.dataset.busy = 'true';
    button.setAttribute('aria-busy', 'true');
    try {
      await addBundle(button, choice);
    } finally {
      button.dataset.busy = 'false';
      button.removeAttribute('aria-busy');
    }
  },
  true
);
